"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import type { LaptopInformation, LaptopScores } from "@/lib/types";
import type { Answer, Reference } from "@/lib/match-explanations";
import { SITE_URL } from "@/lib/utils";
type Props = {
  item: LaptopInformation;
  scores: LaptopScores;
  reference: Reference;
  answers: Record<string, Answer>;
  allItems: LaptopInformation[];
  country: string;
  series: string;
  config: string;
};
type EmailState = "idle" | "sending" | "sent" | "error";
function IconDownload() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-4 shrink-0"><path d="M8 1.5v9m0 0 3-3m-3 3-3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M2 11v2.5h12V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconShare() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-4 shrink-0"><path d="M8 10.5V2m0 0L5 5m3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M3 8v5.5h10V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconMail() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-4 shrink-0"><rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" /><path d="m2 4.5 6 4 6-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function ReportActions({
  item,
  scores,
  reference,
  answers,
  allItems,
  country,
  series,
  config
}: Props) {
  const blobRef = useRef<Blob | null>(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canShareFiles, setCanShareFiles] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailState, setEmailState] = useState<EmailState>("idle");
  const [emailError, setEmailError] = useState<string | null>(null);
  const specUrl = `${SITE_URL}/spec/${country}/${series}/${config}`;
  const getBlob = useCallback(async (): Promise<Blob> => {
    if (blobRef.current) return blobRef.current;
    // pdf-lib and the layout code land in their own chunk, fetched on first use.
    const {
      buildLaptopReportPdf
    } = await import("@/lib/pdf/laptop-report");
    const blob = await buildLaptopReportPdf({
      item,
      scores,
      reference,
      answers,
      allItems,
      country,
      series,
      config
    });
    blobRef.current = blob;
    return blob;
  }, [item, scores, reference, answers, allItems, country, series, config]);

  // Build the PDF ahead of time, while the browser is idle.
  //
  // This is a correctness requirement on iOS, not an optimisation: Safari only
  // honours navigator.share() inside the transient activation window opened by
  // the tap, and awaiting a multi-hundred-millisecond PDF build blows through
  // it, yielding NotAllowedError. Prewarming means the share handler's getBlob()
  // resolves from cache and share() fires effectively synchronously.
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      void getBlob().catch(() => {/* surfaced on click instead */});
    };
    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(warm);
    } else {
      // Safari < 16.4 has no requestIdleCallback.
      setTimeout(warm, 1);
    }
    return () => {
      cancelled = true;
    };
  }, [getBlob]);

  // Probe share support once, on the client. Calling navigator.* during render
  // would break the server render and cause a hydration mismatch.
  useEffect(() => {
    try {
      const probe = new File([new Blob([])], "probe.pdf", {
        type: "application/pdf"
      });
      setCanShareFiles(Boolean(navigator.canShare?.({
        files: [probe]
      })));
    } catch {
      setCanShareFiles(false);
    }
  }, []);
  const filename = useCallback(async () => {
    const {
      reportFilename
    } = await import("@/lib/pdf/laptop-report");
    return reportFilename(item);
  }, [item]);
  const handleDownload = async () => {
    setError(null);
    setBuilding(true);
    try {
      const blob_0 = await getBlob();
      const name = await filename();
      const url = URL.createObjectURL(blob_0);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      console.error("Report build failed:", err);
      setError("We couldn't build your report. Please try again.");
    } finally {
      setBuilding(false);
    }
  };
  const handleShare = async () => {
    setError(null);
    try {
      const blob_1 = await getBlob();
      const name_0 = await filename();
      const file = new File([blob_1], name_0, {
        type: "application/pdf"
      });
      if (!navigator.canShare?.({
        files: [file]
      })) {
        setCanShareFiles(false);
        return;
      }
      await navigator.share({
        files: [file],
        title: `${item.laptop.name} — my laptop match report`,
        text: "Here's the laptop Plample matched me with, and why it fits."
      });
    } catch (err_0) {
      // The user dismissing the share sheet is not an error.
      if (err_0 instanceof DOMException && err_0.name === "AbortError") return;
      setError("Sharing didn't work. You can download the report instead.");
    }
  };
  const handleEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setEmailState("sending");
    setEmailError(null);
    try {
      const blob_2 = await getBlob();
      const name_1 = await filename();
      const payload = new FormData();
      payload.append("pdf", blob_2, name_1);
      payload.append("to", String(data.get("to") ?? ""));
      payload.append("senderName", String(data.get("senderName") ?? ""));
      payload.append("note", String(data.get("note") ?? ""));
      payload.append("company", String(data.get("company") ?? ""));
      payload.append("laptopName", item.laptop.name);
      payload.append("specUrl", specUrl);
      const res = await fetch("/api/share-report", {
        method: "POST",
        body: payload
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        setEmailError(body?.error ?? "We couldn't send that. Please try again.");
        setEmailState("error");
        return;
      }
      setEmailState("sent");
      form.reset();
    } catch {
      setEmailError("We couldn't send that. Please try again.");
      setEmailState("error");
    }
  };
  return <div className="rounded-2xl border border-border bg-surface/50 p-4">
      <p className="text-sm font-semibold text-foreground">Take this with you</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        A one-page report with this laptop&apos;s score, why it fits how you&apos;ll use it, and where
        to buy it. Handy if someone else is paying.
      </p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button onClick={handleDownload} disabled={building} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-foreground text-sm font-semibold text-background transition-opacity hover:opacity-90 active:scale-[0.99] disabled:opacity-60">
          <IconDownload />
          {building ? "Preparing…" : "Download PDF"}
        </button>

        {canShareFiles && <button onClick={handleShare} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-card text-sm font-semibold text-foreground transition-colors hover:border-foreground/40 active:scale-[0.99]">
            <IconShare />
            Share
          </button>}

        <button onClick={() => {
        setEmailOpen(v => !v);
        setEmailState("idle");
      }} aria-expanded={emailOpen} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-card text-sm font-semibold text-foreground transition-colors hover:border-foreground/40 active:scale-[0.99]">
          <IconMail />
          Email it
        </button>
      </div>

      {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}

      {emailOpen && <form onSubmit={handleEmail} className="mt-3 space-y-2 border-t border-border/60 pt-3">
          <p className="text-xs font-medium text-foreground">
            Buying this for someone else — or is someone buying it for you? Send them the report.
          </p>

          <input type="email" name="to" required placeholder="Their email address" className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-foreground/40" />
          <input type="text" name="senderName" maxLength={60} placeholder="Your name (optional)" className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-foreground/40" />
          <textarea name="note" maxLength={500} rows={2} placeholder="Add a note (optional)" className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground/40" />

          {/* Honeypot — hidden from people, tempting to bots. */}
          <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

          <button type="submit" disabled={emailState === "sending"} className="flex h-10 w-full items-center justify-center rounded-xl bg-brand text-sm font-semibold text-brand-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
            {emailState === "sending" ? "Sending…" : "Send the report"}
          </button>

          {emailState === "sent" && <p className="text-xs font-medium text-brand">Sent. It should land in their inbox shortly.</p>}
          {emailState === "error" && emailError && <p className="text-xs font-medium text-red-600">{emailError}</p>}
        </form>}
    </div>;
}